import { useState } from "react";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  DEFAULT_POCKET_COLOR,
  DEFAULT_POCKET_ICON,
  POCKET_COLOR_PALETTE,
  POCKET_ICONS,
} from "../constants/pockets.constants";
import { PocketsService } from "../services/pocketsService";
import type { Pocket, WalletIcon } from "../types/pockets-api.types";
import { PocketFormFields } from "./PocketFormFields";

const ALLOWED_ICONS = POCKET_ICONS.map((d) => d.id) as [WalletIcon, ...WalletIcon[]];
const ALLOWED_COLORS = POCKET_COLOR_PALETTE as readonly string[];

const schema = z.object({
  name: z.string().min(1).max(255),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .refine((v) => ALLOWED_COLORS.includes(v), {
      message: "Color must be one of the picker swatches.",
    }),
  icon: z.enum(ALLOWED_ICONS),
});

type Values = z.infer<typeof schema>;

const DEFAULTS: Values = {
  name: "",
  color: DEFAULT_POCKET_COLOR,
  icon: DEFAULT_POCKET_ICON,
};

type CreatePocketModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (pocket: Pocket) => void;
};

const FORM_ID = "create-pocket-form";

// Initial balance is not collected — funded post-create via a separate transfer.
export const CreatePocketModal = ({ open, onOpenChange, onCreated }: CreatePocketModalProps) => {
  const { t } = useTranslations("cards");
  const { user } = useAuth();
  const form = useZodForm(schema, DEFAULTS);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values: Values) => {
    const organizationId = user?.organization_id ?? user?.organization?.id;
    if (!organizationId) {
      colocaToast.error(t("pocket_create_no_org"));
      return;
    }

    setSubmitting(true);
    try {
      const response = await PocketsService.createPocket({
        organization_id: organizationId,
        name: values.name.trim(),
        color: values.color,
        icon: values.icon,
      });
      colocaToast.success(t("pocket_create_success"));
      onCreated?.(response.data);
      onOpenChange(false);
      form.reset(DEFAULTS);
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("pocket_create_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("pocket_create_modal_title")}
      description={t("pocket_create_modal_description")}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("create_identity_modal_button_cancel")}
          </ColocaButton>
          <ColocaButton
            type="submit"
            variant="primary"
            form={FORM_ID}
            loading={submitting}
            disabled={submitting}
          >
            {t("pocket_create_modal_submit")}
          </ColocaButton>
        </div>
      }
    >
      <ColocaForm form={form} onSubmit={handleSubmit} id={FORM_ID}>
        <PocketFormFields />
      </ColocaForm>
    </ColocaDialog>
  );
};
